import { Component, Input, OnChanges, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ApiSynchronizerService, SynchronizerHistoryItem } from '../../../../shared/services/api-synchronizer.service';
import { Environments } from '../../../../shared/models/environments.type';
import { SyncEntities } from '../../../../shared/models/sync-entities.type';

interface SyncDirectionRow {
  source: Environments;
  destination: Environments;
  latest: SynchronizerHistoryItem | null;
}

type EntityStatus = 'complete' | 'failed' | 'skipped' | 'in-progress' | 'pending';

@Component({
  selector: 'app-latest-synchronizations-card',
  imports: [CommonModule],
  templateUrl: './latest-synchronizations-card.component.html',
})
export class LatestSynchronizationsCardComponent implements OnChanges {
  @Input({ required: true }) organizationId!: string;

  // Same two hops the synchronizer page actually allows — dev to qa, or qa to eu. Unlike the delta
  // comparison widgets (which can diff any two environments), a sync can never jump dev straight to eu.
  readonly directionOptions: { source: Environments; destination: Environments }[] = [
    { source: 'dev', destination: 'qa' },
    { source: 'qa', destination: 'eu' },
  ];

  loading = signal(false);
  error = signal<string | null>(null);
  rows: SyncDirectionRow[] = [];

  constructor(private synchronizerService: ApiSynchronizerService) {}

  ngOnChanges(): void {
    if (this.organizationId) this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.synchronizerService.syncHistory(this.organizationId, 1, 50).subscribe({
      next: (result) => {
        const sorted = [...result.items].sort((a, b) => new Date(b.generatedOn).getTime() - new Date(a.generatedOn).getTime());
        this.rows = this.directionOptions.map(dir => ({
          ...dir,
          // sourceEnvironment/destinationEnvironment are typed as plain string (not the lowercase
          // Environments literal) because the API doesn't guarantee casing — compare case-insensitively.
          latest: sorted.find(i =>
            i.sourceEnvironment.toLowerCase() === dir.source
            && i.destinationEnvironment.toLowerCase() === dir.destination
          ) ?? null,
        }));
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load synchronization history. | ' + err);
        this.loading.set(false);
      }
    });
  }

  formatEntity(entity: SyncEntities): string {
    return entity.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  }

  // Whatever this sync actually touched — the union of what export and import each reported.
  entitiesFor(item: SynchronizerHistoryItem): SyncEntities[] {
    const set = new Set<SyncEntities>();
    item.export.forEach(d => set.add(d.entity));
    item.import.forEach(d => set.add(d.entity));
    return Array.from(set);
  }

  // Import status wins once it exists — it's the more current stage of the sync. Falls back to
  // export status while import hasn't been requested yet, matching the synchronizer page's logic.
  entityStatus(item: SynchronizerHistoryItem, entity: SyncEntities): EntityStatus {
    const importDetail = item.import.find(d => d.entity === entity);
    if (importDetail) {
      if (importDetail.failureCount > 0) return 'failed';
      if (importDetail.skippedCount === importDetail.totalCount && importDetail.successCount === 0) return 'skipped';
      if (importDetail.completedAt) return 'complete';
      return 'in-progress';
    }
    const exportDetail = item.export.find(d => d.entity === entity);
    if (exportDetail) {
      if (exportDetail.failureCount > 0) return 'failed';
      if (exportDetail.completedAt) return 'complete';
      return 'in-progress';
    }
    return 'pending';
  }
}
